import { useState, useEffect } from 'react'
import { useAuthStore } from '../../store/authStore'
import { AlertTriangle, TrendingUp, Clock, CheckCircle, Filter } from 'lucide-react'

const SupervisionMejoraContinua = () => {
  const { token } = useAuthStore()
  const [brechas, setBrechas] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [filters, setFilters] = useState({
    programa: '',
    estado: '',
    severidad: ''
  })

  useEffect(() => {
    fetchAllBrechas()
  }, [token])

  const fetchAllBrechas = async () => {
    try {
      setIsLoading(true)
      // TODO: Implementar endpoint /api/brechas/all cuando esté disponible
      // Por ahora mostramos un mensaje
      setError('Endpoint de supervisión en desarrollo. Disponible en próxima fase.')
      setIsLoading(false)
    } catch (err) {
      setError(err.message)
      setIsLoading(false)
    }
  }

  const getSeveridadColor = (severidad) => {
    switch (severidad) {
      case 'alta': return 'bg-red-100 text-red-800 border-red-200'
      case 'media': return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      case 'baja': return 'bg-blue-100 text-blue-800 border-blue-200'
      default: return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'abierta': return 'bg-red-100 text-red-800'
      case 'en_proceso': return 'bg-yellow-100 text-yellow-800'
      case 'resuelta': return 'bg-green-100 text-green-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="card">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="h-6 w-6 text-purple-600" />
            </div>
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-900 mb-1">
              Supervisión de Mejora Continua
            </h2>
            <p className="text-sm text-gray-600">
              Vista consolidada de todas las brechas y ciclos PDCA del sistema (solo lectura)
            </p>
          </div>
        </div>

        <div className="mt-4 bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
          <p className="text-sm text-blue-800">
            <strong>ℹ️ Modo Supervisión:</strong> Como administrador, puedes ver todas las brechas 
            pero no puedes crear, editar o eliminar. Los usuarios son responsables de gestionar sus brechas.
          </p>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="h-8 w-8 text-red-500" />
            <div>
              <p className="text-sm text-gray-600">Brechas Abiertas</p>
              <p className="text-2xl font-bold text-gray-900">-</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center space-x-3">
            <Clock className="h-8 w-8 text-yellow-500" />
            <div>
              <p className="text-sm text-gray-600">En Proceso</p>
              <p className="text-2xl font-bold text-gray-900">-</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center space-x-3">
            <CheckCircle className="h-8 w-8 text-green-500" />
            <div>
              <p className="text-sm text-gray-600">Resueltas</p>
              <p className="text-2xl font-bold text-gray-900">-</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center space-x-3">
            <TrendingUp className="h-8 w-8 text-purple-500" />
            <div>
              <p className="text-sm text-gray-600">Ciclos PDCA</p>
              <p className="text-2xl font-bold text-gray-900">-</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="card">
        <div className="flex items-center space-x-2 mb-4">
          <Filter className="h-5 w-5 text-gray-500" />
          <h3 className="text-lg font-semibold text-gray-900">Filtros</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Programa
            </label>
            <select
              value={filters.programa}
              onChange={(e) => setFilters({ ...filters, programa: e.target.value })}
              className="input-field"
            >
              <option value="">Todos los programas</option>
              <option value="kinesiologia">Kinesiología</option>
              <option value="enfermeria">Enfermería</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Estado
            </label>
            <select
              value={filters.estado}
              onChange={(e) => setFilters({ ...filters, estado: e.target.value })}
              className="input-field"
            >
              <option value="">Todos los estados</option>
              <option value="abierta">Abierta</option>
              <option value="en_proceso">En Proceso</option>
              <option value="resuelta">Resuelta</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Severidad
            </label>
            <select
              value={filters.severidad}
              onChange={(e) => setFilters({ ...filters, severidad: e.target.value })}
              className="input-field"
            >
              <option value="">Todas las severidades</option>
              <option value="alta">Alta</option>
              <option value="media">Media</option>
              <option value="baja">Baja</option>
            </select>
          </div>
        </div>
      </div>

      {/* Mensaje de desarrollo */}
      {error && (
        <div className="card">
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
              <div>
                <h3 className="text-sm font-semibold text-yellow-800 mb-1">
                  Funcionalidad en Desarrollo
                </h3>
                <p className="text-sm text-yellow-700">
                  {error}
                </p>
                <p className="text-sm text-yellow-700 mt-2">
                  Esta vista estará disponible cuando se implementen las siguientes tareas:
                </p>
                <ul className="list-disc list-inside text-sm text-yellow-700 mt-2 space-y-1">
                  <li>Endpoint GET /api/brechas/all (Task 6.5)</li>
                  <li>Endpoint GET /api/admin/mejora-continua/metricas (Task 9.6)</li>
                  <li>Componentes de supervisión completos (Phase 14)</li>
                </ul>
                <p className="text-sm text-yellow-700 mt-2">
                  Por ahora, los usuarios pueden gestionar sus brechas desde el Panel de Usuario.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabla de brechas (placeholder) */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Todas las Brechas del Sistema
        </h3>
        
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <TrendingUp className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">
            La tabla de brechas se mostrará aquí cuando se implemente el endpoint de supervisión.
          </p>
        </div>
      </div>
    </div>
  )
}

export default SupervisionMejoraContinua
